import React, { useState } from 'react';
import { Paper, Typography, Box, CircularProgress } from '@mui/material';
import CompareIcon from '@mui/icons-material/Compare';
import Anthropic from '@anthropic-ai/sdk';
import { ARUBA_CRITERIA } from '../Data/Criteria_Aruba';
import { CISCO_CRITERIA } from '../Data/Criteria_Cisco';
import { JUNIPER_CRITERIA } from '../Data/Criteria_Juniper';
import { PALO_ALTO_CRITERIA } from '../Data/Criteria_Palo_Alto';

const TechnologyComparison = ({ onAnalysisComplete }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentCriteria, setCurrentCriteria] = useState('');
  const [progress, setProgress] = useState(0);
  
  const getCriteriaGroups = () => {
    const allCriteria = new Set();
    
    [ARUBA_CRITERIA.ARUBA, CISCO_CRITERIA.CISCO, 
     JUNIPER_CRITERIA.JUNIPER, PALO_ALTO_CRITERIA.PALO_ALTO].forEach(vendor => {
      vendor.forEach(item => allCriteria.add(item.Criteria));
    });

    const groups = {};
    allCriteria.forEach(criteria => {
      groups[criteria] = {
        aruba: ARUBA_CRITERIA.ARUBA.filter(item => item.Criteria === criteria),
        cisco: CISCO_CRITERIA.CISCO.filter(item => item.Criteria === criteria),
        juniper: JUNIPER_CRITERIA.JUNIPER.filter(item => item.Criteria === criteria),
        paloAlto: PALO_ALTO_CRITERIA.PALO_ALTO.filter(item => item.Criteria === criteria)
      };
    });

    return groups;
  };

  const analyzeCriteriaGroup = async (criteria, vendorData) => {
    try {
      const client = new Anthropic({
        apiKey: "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",
        dangerouslyAllowBrowser: true
      });

      const prompt = `Please analyze and compare the following vendor data for the "${criteria}" criteria:
      
      Aruba: ${JSON.stringify(vendorData.aruba)}
      Cisco: ${JSON.stringify(vendorData.cisco)}
      Juniper: ${JSON.stringify(vendorData.juniper)}
      Palo Alto: ${JSON.stringify(vendorData.paloAlto)}
      
      Please provide:
      1. A detailed comparison of how each vendor handles this criteria
      2. Notable strengths and weaknesses for each vendor
      3. Key differentiators
      4. Best overall approach for this criteria
      5. Recommendations based on the data
      
      Format your response in a clear, structured manner using markdown formatting.`;

      const response = await client.messages.create({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 4096,
        messages: [{
          role: 'user',
          content: prompt
        }],
        temperature: 0.0
      });

      return {
        criteria,
        analysis: response.content[0].text
      };

    } catch (error) {
      console.error(`Error analyzing ${criteria}:`, error);
      return {
        criteria,
        analysis: `Error analyzing ${criteria}. Please try again.`
      };
    }
  };

  const handleCompareClick = async () => {
    setIsAnalyzing(true);
    const results = [];
    const groups = getCriteriaGroups();
    const totalCriteria = Object.keys(groups).length;
    let completed = 0;

    try {
      for (const [criteria, vendorData] of Object.entries(groups)) {
        setCurrentCriteria(criteria);
        const analysis = await analyzeCriteriaGroup(criteria, vendorData);
        results.push(analysis);
        
        completed++;
        setProgress((completed / totalCriteria) * 100);
      }

      // Combine all results into a single comprehensive analysis
      const combinedAnalysis = results.map(result => (
        `## ${result.criteria}\n\n${result.analysis}\n\n---\n\n`
      )).join('\n');

      onAnalysisComplete(combinedAnalysis);
    } catch (error) {
      console.error('Error in technology comparison:', error);
      onAnalysisComplete('Error performing technology comparison. Please try again.');
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
      setCurrentCriteria('');
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        cursor: isAnalyzing ? 'default' : 'pointer',
        position: 'relative',
        mt: 2
      }}
      onClick={!isAnalyzing ? handleCompareClick : undefined}
    >
      {isAnalyzing ? (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>
              Analyzing {currentCriteria}... ({Math.round(progress)}%)
            </Typography>
          </Box>
          <Box sx={{ 
            width: '100%', 
            height: 4, 
            bgcolor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <Box
              sx={{
                width: `${progress}%`,
                height: '100%',
                bgcolor: 'primary.main',
                transition: 'width 0.5s ease-in-out'
              }}
            />
          </Box>
        </Box>
      ) : (
        <>
          <CompareIcon sx={{ mr: 1 }} />
          <Typography>Compare Providers</Typography>
        </>
      )}
    </Paper>
  );
};

export default TechnologyComparison;